import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const ForgotPasswordForm = ({ onBackToLogin, onSuccess }) => {
  const [step, setStep] = useState('email'); // email, verification, reset
  const [formData, setFormData] = useState({
    email: '',
    verificationCode: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  // Mock verification code
  const mockVerificationCode = '123456';

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateEmail = () => {
    const newErrors = {};
    
    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const validateVerification = () => {
    const newErrors = {};
    
    if (!formData?.verificationCode) {
      newErrors.verificationCode = 'Verification code is required';
    } else if (formData?.verificationCode?.length !== 6) {
      newErrors.verificationCode = 'Please enter a 6-digit code';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const validateReset = () => {
    const newErrors = {};
    
    if (!formData?.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData?.newPassword?.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    }
    
    if (formData?.newPassword !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleEmailSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateEmail()) return;
    
    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      setStep('verification');
      setResendTimer(60);
      setIsLoading(false);
      
      // Start countdown
      const interval = setInterval(() => {
        setResendTimer(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, 1500);
  };

  const handleVerificationSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateVerification()) return;
    
    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      if (formData?.verificationCode === mockVerificationCode) {
        setStep('reset');
      } else {
        setErrors({
          verificationCode: `Invalid code. Use ${mockVerificationCode} for demo`
        });
      }
      setIsLoading(false);
    }, 1000);
  };

  const handleResetSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateReset()) return;
    
    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      onSuccess();
      setIsLoading(false);
    }, 1500);
  };

  const handleResendCode = () => {
    if (resendTimer > 0) return;
    
    setResendTimer(60);
    const interval = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  if (step === 'email') {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Lock" size={24} className="text-primary" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Forgot Password?</h2>
          <p className="text-muted-foreground">
            No worries! Enter your email and we'll send you a reset link.
          </p>
        </div>
        <form onSubmit={handleEmailSubmit} className="space-y-6">
          <Input
            label="Email Address"
            type="email"
            name="email"
            placeholder="Enter your email address"
            value={formData?.email}
            onChange={handleInputChange}
            error={errors?.email}
            required
          />

          <Button
            type="submit"
            loading={isLoading}
            fullWidth
            className="h-12"
          >
            Send Reset Code
          </Button>
        </form>
        <div className="mt-8 text-center">
          <button
            onClick={onBackToLogin}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center space-x-1"
          >
            <Icon name="ArrowLeft" size={16} />
            <span>Back to sign in</span>
          </button>
        </div>
      </div>
    );
  }

  if (step === 'verification') {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Mail" size={24} className="text-primary" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Check Your Email</h2>
          <p className="text-muted-foreground">
            We've sent a 6-digit verification code to{' '}
            <span className="font-medium text-foreground">{formData?.email}</span>
          </p>
        </div>
        <form onSubmit={handleVerificationSubmit} className="space-y-6">
          <Input
            label="Verification Code"
            type="text"
            name="verificationCode"
            placeholder="Enter 6-digit code"
            value={formData?.verificationCode}
            onChange={handleInputChange}
            error={errors?.verificationCode}
            maxLength={6}
            required
          />

          <Button
            type="submit"
            loading={isLoading}
            fullWidth
            className="h-12"
          >
            Verify Code
          </Button>
        </form>
        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground mb-2">
            Didn't receive the code?
          </p>
          <button
            onClick={handleResendCode}
            disabled={resendTimer > 0}
            className="text-sm text-primary hover:text-primary/80 disabled:text-muted-foreground disabled:cursor-not-allowed transition-colors"
          >
            {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend code'}
          </button>
        </div>
        <div className="mt-8 text-center">
          <button
            onClick={onBackToLogin}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center space-x-1"
          >
            <Icon name="ArrowLeft" size={16} />
            <span>Back to sign in</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Key" size={24} className="text-success" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Reset Password</h2>
        <p className="text-muted-foreground">
          Create a new password for your account
        </p>
      </div>
      <form onSubmit={handleResetSubmit} className="space-y-6">
        <Input
          label="New Password"
          type="password"
          name="newPassword"
          placeholder="Enter new password"
          value={formData?.newPassword}
          onChange={handleInputChange}
          error={errors?.newPassword}
          required
        />

        <Input
          label="Confirm New Password"
          type="password"
          name="confirmPassword"
          placeholder="Confirm new password"
          value={formData?.confirmPassword}
          onChange={handleInputChange}
          error={errors?.confirmPassword}
          required
        />

        <Button
          type="submit"
          loading={isLoading}
          fullWidth
          className="h-12"
        >
          Reset Password
        </Button>
      </form>
      <div className="mt-8 text-center">
        <button
          onClick={onBackToLogin}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center space-x-1"
        >
          <Icon name="ArrowLeft" size={16} />
          <span>Back to sign in</span>
        </button>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;